import { EventEmitterInterface } from '../event/emitter.interface';
import { HttpParameters } from './parameters';
import { HttpRequestInterface } from './request.interface';
import { DataKeyValueStringInterface } from '../data/key-value/string.interface';
import { HttpApiResponseInterface } from './api-response.interface';
import { HttpRequestEvent } from './request.event';

export class HttpRequest implements HttpRequestInterface {
  public headers: DataKeyValueStringInterface = {};
  public cookieUsage: boolean = false;
  private isJsonApiRequest: boolean = false;

  /**
   *
   * @param xmlHttpRequest
   * @param eventEmitter
   */
  constructor(private xmlHttpRequest: XMLHttpRequest, private eventEmitter: EventEmitterInterface) {}

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritDoc
   */
  public setHeader(key: string, value: string): void {
    this.headers[key] = value;
  }

  /**
   * @inheritDoc
   */
  public unsetHeader(key: string): void {
    delete this.headers[key];
  }

  /**
   * @inheritDoc
   */
  public setCookieUsage(cookieUsage: boolean): void {
    this.cookieUsage = cookieUsage;
  }

  /**
   * @inheritDoc
   */
  public setRequestType(isJsonApiRequest: boolean): void {
    this.isJsonApiRequest = isJsonApiRequest;
  }

  /**
   * @inheritDoc
   */
  public send(method: string, url: string, httpParameters: HttpParameters): void {
    // Method
    method = method.toUpperCase();

    // Query string
    let payload = httpParameters.toQueryString();

    // Append query string to URL if method is GET
    if (method === 'GET') {
      url += '?' + payload;
    } else {
      payload = httpParameters.toPayloadString();
    }

    // Prepare request
    this.xmlHttpRequest.open(method.toUpperCase(), url, true);

    // Cookie usage
    this.xmlHttpRequest.withCredentials = this.cookieUsage;

    // Always add X-Requested-With header
    this.setHeader('X-Requested-With', 'XMLHttpRequest');

    // Set headers
    Object.keys(this.headers).forEach((key) => {
      this.xmlHttpRequest.setRequestHeader(key, this.headers[key]);
    });

    // Listeners
    this.xmlHttpRequest.onload = this.onLoad.bind(this);
    this.xmlHttpRequest.onerror = this.onError.bind(this);
    this.xmlHttpRequest.onabort = this.onAbort.bind(this);

    // Send request
    this.xmlHttpRequest.send(payload);
  }

  /**
   * @inheritDoc
   */
  public upload(method: string, url: string, fieldName: string, file: Blob): void {
    const formData = new FormData();
    formData.set(fieldName, file);

    // Prepare request
    this.xmlHttpRequest.open(method.toUpperCase(), url);

    // Listeners
    this.xmlHttpRequest.onload = this.onLoad.bind(this);
    this.xmlHttpRequest.onerror = this.onError.bind(this);
    this.xmlHttpRequest.onabort = this.onAbort.bind(this);

    this.xmlHttpRequest.send(formData);
  }

  /**
   * @inheritdoc
   */
  public abort(): void {
    this.xmlHttpRequest.abort();
  }

  /**
   * @inheritDoc
   */
  public success(): void {
    const response = this.prepareResponse();

    if (this.isJsonApiRequest && !(response.data instanceof Object)) {
      this.eventEmitter.emit(HttpRequestEvent.failure, response);
    } else {
      this.eventEmitter.emit(HttpRequestEvent.success, response);
    }
  }

  /**
   * @inheritDoc
   */
  public failure(): void {
    const response = this.prepareResponse(true);
    this.eventEmitter.emit(HttpRequestEvent.failure, response);
  }

  /**
   * @inheritDoc
   */
  public onLoad(): void {
    this.xmlHttpRequest.status < 300 ? this.success() : this.failure();
  }

  /**
   * @inheritDoc
   */
  public onError(): void {
    this.failure();
  }

  /**
   * @inheritdoc
   */
  public onAbort(): void {
    const response: HttpApiResponseInterface = {
      data: {},
      status: 0,
      statusText: 'abort',
      error: false,
      headers: {},
      request: this.xmlHttpRequest,
    };
    this.eventEmitter.emit(HttpRequestEvent.abort, response);
  }

  /**
   * Prepare response
   * @param isError
   */
  private prepareResponse(isError: boolean = false): HttpApiResponseInterface {
    const response: HttpApiResponseInterface = {
      data: this.xmlHttpRequest.responseText || {},
      status: this.xmlHttpRequest.status,
      statusText: this.xmlHttpRequest.statusText,
      error: isError,
      headers: this.getHeadersAsObject(),
      request: this.xmlHttpRequest,
    };

    try {
      response.data = JSON.parse(this.xmlHttpRequest.responseText);
    } catch (e) {
      // Throw Error in the future when we will support only jsonAPI
    }

    return response;
  }

  /**
   * Get XMLHttpRequest headers as a readable object
   */
  private getHeadersAsObject(): DataKeyValueStringInterface {
    const headers: DataKeyValueStringInterface = {};
    const delimiter = '\u003a\u0020'; // :
    const newLine = '\u000d\u000a'; // CR LF

    this.xmlHttpRequest
      .getAllResponseHeaders()
      .split(newLine)
      .forEach((line) => {
        if (line.length > 0) {
          const header = line.split(delimiter);
          const headerName = header.shift().toLowerCase();

          headers[headerName] = header.join(delimiter);
        }
      });

    return headers;
  }
}
